import { Injectable } from '@angular/core';

/**
 * How bright an uploaded logo is, which decides the plate drawn behind it.
 * `light` → needs a dark plate, `dark` → needs a light plate, `neutral` →
 * mid-tone or undeterminable, use the safe default plate.
 */
export type LogoTone = 'light' | 'dark' | 'neutral';

/**
 * Samples an uploaded logo and reports whether it is light or dark, so callers
 * can put a contrasting plate behind it. Without this a white logo vanishes on
 * a light event banner and a black one vanishes on a dark banner.
 *
 * Results are cached per URL, and concurrent requests for the same URL share a
 * single decode.
 */
@Injectable({ providedIn: 'root' })
export class LogoToneService {
  /** Sampling grid. 32x32 is plenty for an average and keeps decode cheap. */
  private static readonly SAMPLE_SIZE = 32;
  /** Pixels fainter than this are logo padding, not artwork. */
  private static readonly MIN_ALPHA = 16;
  /** Below this share of opaque pixels the average is not trustworthy. */
  private static readonly MIN_OPAQUE_RATIO = 0.02;
  /** WCAG relative luminance thresholds for "clearly light" / "clearly dark". */
  private static readonly LIGHT_ABOVE = 0.6;
  private static readonly DARK_BELOW = 0.25;

  private readonly cache = new Map<string, LogoTone>();
  private readonly inFlight = new Map<string, Promise<LogoTone>>();

  /**
   * Resolves the tone for `url`. Never rejects — anything unexpected (CORS,
   * decode failure, no browser APIs) resolves to `neutral` so the caller
   * always gets a usable plate.
   */
  resolve(url: string): Promise<LogoTone> {
    if (!url) {
      return Promise.resolve('neutral');
    }

    const cached = this.cache.get(url);
    if (cached) {
      return Promise.resolve(cached);
    }

    const pending = this.inFlight.get(url);
    if (pending) {
      return pending;
    }

    const request = this.analyse(url)
      .catch(() => 'neutral' as LogoTone)
      .then((tone) => {
        this.cache.set(url, tone);
        this.inFlight.delete(url);
        return tone;
      });

    this.inFlight.set(url, request);
    return request;
  }

  private analyse(url: string): Promise<LogoTone> {
    const doc = globalThis.document;
    if (!doc) {
      // SSR — no canvas, decide on the client instead.
      return Promise.resolve('neutral');
    }

    return this.decode(url).then((image) =>
      this.toneFromPixels(this.samplePixels(image, doc))
    );
  }

  private decode(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const probe = new Image();
      // Required to read pixels back. If the host does not answer with CORS
      // headers the probe errors out and we fall back to `neutral`; the
      // visible <img> is a separate element and is unaffected either way.
      probe.crossOrigin = 'anonymous';
      probe.onload = () => resolve(probe);
      probe.onerror = () => reject(new Error(`logo decode failed: ${url}`));
      probe.src = url;
      // A cached image can already be complete before the handlers attach.
      if (probe.complete && probe.naturalWidth > 0) {
        resolve(probe);
      }
    });
  }

  /** Throws on a tainted canvas; `resolve()` turns that into `neutral`. */
  private samplePixels(image: HTMLImageElement, doc: Document): Uint8ClampedArray {
    const size = LogoToneService.SAMPLE_SIZE;
    const canvas = doc.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      throw new Error('2d context unavailable');
    }

    context.drawImage(image, 0, 0, size, size);
    return context.getImageData(0, 0, size, size).data;
  }

  private toneFromPixels(data: Uint8ClampedArray): LogoTone {
    let weighted = 0;
    let weight = 0;
    let opaque = 0;

    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3];
      // Transparent padding surrounds most logos. Averaging it in would drag a
      // white logo toward black and pick the wrong plate.
      if (alpha < LogoToneService.MIN_ALPHA) {
        continue;
      }
      opaque++;
      const share = alpha / 255;
      weighted +=
        this.relativeLuminance(data[i], data[i + 1], data[i + 2]) * share;
      weight += share;
    }

    const total = data.length / 4;
    if (!weight || opaque / total < LogoToneService.MIN_OPAQUE_RATIO) {
      return 'neutral';
    }

    const luminance = weighted / weight;
    if (luminance >= LogoToneService.LIGHT_ABOVE) {
      return 'light';
    }
    if (luminance <= LogoToneService.DARK_BELOW) {
      return 'dark';
    }
    return 'neutral';
  }

  /** WCAG 2.x relative luminance — perceptual, unlike a raw RGB mean. */
  private relativeLuminance(r: number, g: number, b: number): number {
    const linear = (channel: number): number => {
      const value = channel / 255;
      return value <= 0.04045
        ? value / 12.92
        : Math.pow((value + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
  }
}
